import { startupArrayToCommaText } from '@/components-biz/ImageSelect';
import { type StartupArgFormRow, normalizeStartupArgDefinitions } from '@/delivery-type-plugin/capabilities/startup-command-args/utils';
import { parseContainerDeployConfigObject } from '@/delivery-type-plugin/utils/parseContainerDeployConfigObject';

export interface ParsedStartupCommandItem {
  commandText?: string;
  args?: unknown[];
}

function parseStartupCommandItemFromRaw(item: unknown): ParsedStartupCommandItem | undefined {
  if (!item || typeof item !== 'object') return undefined;
  const row = item as { command?: unknown; args?: unknown };
  let commandText: string | undefined;
  if (Array.isArray(row.command)) {
    commandText = startupArrayToCommaText(row.command) || undefined;
  } else if (typeof row.command === 'string' && row.command.trim()) {
    commandText = row.command.trim();
  }
  const args = row.args;
  const hasArgs = Array.isArray(args) && args.length > 0;
  if (!commandText && !hasArgs) return undefined;
  return { commandText, args: hasArgs ? args : undefined };
}

function readStartupCommandItemForRole(
  startup: Record<string, unknown> | undefined,
  role: 'master' | 'worker',
): ParsedStartupCommandItem | undefined {
  if (!startup || typeof startup !== 'object') return undefined;
  const primarySecondary = startup.primarySecondary as Record<string, unknown> | undefined;
  const candidates =
    role === 'master'
      ? [primarySecondary?.master, startup.distributed, startup.normal, startup.single]
      : [primarySecondary?.worker, startup.normal, startup.single];
  for (const item of candidates) {
    const parsed = parseStartupCommandItemFromRaw(item);
    if (parsed) return parsed;
  }
  return undefined;
}

/** 按节点角色从 containerDeployConfig 解析启动命令 */
export function readDeployConfigStartupCommandForRole(
  raw?: string | Record<string, unknown> | null,
  role: 'master' | 'worker' = 'worker',
): ParsedStartupCommandItem | undefined {
  const config = parseContainerDeployConfigObject(raw);
  const startup = config.startupCommands as Record<string, unknown> | undefined;
  return readStartupCommandItemForRole(startup, role);
}

/** 从 startupCommands 根对象按角色解析（表单 hidden 字段 `_imageContainerDeployStartup`） */
export function readStartupCommandForRoleFromStartupRoot(
  startup: Record<string, unknown> | undefined,
  role: 'master' | 'worker',
): ParsedStartupCommandItem | undefined {
  return readStartupCommandItemForRole(startup, role);
}

/** 将 containerDeployConfig.startupCommands.args 转为带 _self 的表单行 */
export function buildStartupArgFormRowsFromDeployConfigArgs(args: unknown[]): StartupArgFormRow[] {
  if (!Array.isArray(args) || !args.length) return [];
  const definitions = normalizeStartupArgDefinitions(args);
  if (!definitions.length) return [];

  return definitions.map((def, index) => {
    const source = args[index];
    const value =
      source && typeof source === 'object' && !Array.isArray(source)
        ? String((source as { value?: unknown }).value ?? def.defaultValue ?? '')
        : String(def.defaultValue ?? '');
    return {
      name: def.name,
      value,
      _self: JSON.stringify(def),
    };
  });
}
